import { Component, ElementRef, OnInit, ViewChild, HostListener } from '@angular/core';
import { Store } from '@ngrx/store';
import { IState } from '../../shared/models/state';
import {
  getContactsRowsSelector,
  getContactsColsSelector,
  getContactsTotalSelector,
  getContactsPageSelector,
  getTempPageSelector,
  getExpandedListSelector,
  getTempEditedFiltersSelector
} from '../contacts.reducer';
import {
  GetDataAction,
  UpdateColumnsAction,
  SetFilterTagsAction,
  UpdateTempFilterMaxData,
  ClearPageOffsetAction
} from '../contacts.actions';
import { Contact, Column, Page } from '../shared/models/Contact';
import { ContactsService } from '../shared/services/contacts.service';
import * as moment from 'moment';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { DATETIME_FORMATS } from '../../shared/utils/timeUtils';
import { DatePickerService } from '../../shared/services/date.picker.service';

@Component({
  selector: 'cc-events-table',
  templateUrl: './events-table.component.html',
  styleUrls: ['./events-table.component.scss']
})
export class EventsTableComponent implements OnInit {
  public loadingIndicator: true;
  public reorderable: true;
  public expanded: any;
  public selected = [];
  public swapColumns = false;
  public paginationOptions: number[] = [10, 20, 50, 100];
  public rows: Contact[];
  public columns: Column[];
  public total: number;
  public expandedList: number[];
  public page: Page;
  public tempPageFilters: Page;
  public dateDateView: string;
  public utcOffset = moment().utcOffset();
  public maxDate = moment().add(this.utcOffset / 60, 'hours').toISOString();
  public auto: any;
  public emptyMessage: string;
  public messages: { emptyMessage: string };
  public activeEvent: number;
  private minDetailHeight = 530;
  private scrollWidth = 6;
  private scrollOffset: number;

  public startDate: moment.Moment | string;
  public endDate: moment.Moment | string;
  public tableOffset: number;

  @ViewChild('myTable') table: any;
  @ViewChild('openCalendar') iconCalendar: ElementRef;
  public filterParams: Page;
  public tempEditedFiltersList: string[];
  public innerWidth: any;
  public columnWidth: number;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth <= 1500) {
      this.columnWidth = 170;
    } else if (this.innerWidth <= 1750 && this.innerWidth > 1500) {
      this.columnWidth = 190;
    } else if (this.innerWidth <= 1860 && this.innerWidth > 1750) {
      this.columnWidth = 200;
    } else {
      this.columnWidth = 230;
    }
  }
  constructor(
    private store: Store<IState>,
    public contactsService: ContactsService,
    public datePickerService: DatePickerService,
    public translate: TranslateService
  ) {}

  ngOnInit() {
    this.onResize(event);
    this.filterParams = {
      filter: {
        dateFrom: moment()
          .subtract(7, 'day')
          .toISOString(),
        dateTo: moment().toISOString(),
        contactId: null,
        users: []
      },
      pagination: {
        limit: 10,
        offset: 0,
        sortBy: null,
        order: null
      }
    };

    this.store.select(getContactsRowsSelector).subscribe((contacts: Contact[]) => {
      this.rows = contacts;
    });

    this.store.select(getContactsColsSelector).subscribe((columns: Column[]) => {
      this.columns = columns;
    });

    this.store.select(getContactsTotalSelector).subscribe((total: number) => {
      this.total = total;
    });

    this.store.select(getContactsPageSelector).subscribe((page: Page) => {
      this.page = page;
    });

    this.store.select(getExpandedListSelector).subscribe((expandedList: number[]) => {
      this.expandedList = expandedList;
    });

    this.store.select(getTempPageSelector).subscribe((tempPage: Page) => {
      this.tempPageFilters = tempPage;

      if (tempPage) {
        this.filterParams = tempPage;
        this.dateDateView = this.getRangeTagName({
          dateFrom: moment(this.filterParams.filter.dateFrom).toISOString(),
          dateTo: this.filterParams
            ? moment(this.filterParams.filter.dateTo).toISOString()
            : moment().toISOString()
        });
        this.startDate = moment(this.filterParams.filter.dateFrom).toISOString();
        this.endDate = moment(this.filterParams.filter.dateTo).toISOString();
        this.tableOffset = this.filterParams.pagination.offset / this.filterParams.pagination.limit;
      }
    });

    this.contactsService.useFilters.subscribe(() => {
      this.onUseFilters();
    });

    this.translate.get('CONTACTS_LIST.NO_DATA_FOUND').subscribe(text => {
      this.emptyMessage = text;
      this.messages = {
        emptyMessage: `<div class="empty-message"><span>${this.emptyMessage}</span></div>`
      };
    });

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.messages = {
        emptyMessage: `<div class="empty-message"><span>${event.translations.CONTACTS_LIST.NO_DATA_FOUND}</span></div>`
      };
    });

    this.store.select(getTempEditedFiltersSelector).subscribe((tempEditedFiltersList: string[]) => {
      this.tempEditedFiltersList = tempEditedFiltersList;
    });

    this.maxDate = moment().add(this.utcOffset / 60, 'hours').toISOString();

    this.contactsService.fetchDataAction(this.filterParams, 0);
  }

  onActivate(event) {
    this.activeEvent = event.row.event_id;
  }

  getRowIdentity(row) {
    return row.NATIVE_RECORD_ID;
  }

  onSelect({ selected }) {
    this.selected.splice(0, this.selected.length);
    this.selected.push(...selected);
  }

  add() {
    this.selected.push(this.rows[1], this.rows[3]);
  }

  update() {
    this.selected = [this.rows[1], this.rows[3]];
  }

  remove() {
    this.selected = [];
  }

  displayCheck(row) {
    // when want to have some already check by default (uncheckable)
    // return row.userName !== 'Some User Name';
  }

  onSort(event) {
    const colName = event.sorts[0].prop.startsWith('NATIVE')
      ? event.sorts[0].prop.replace('NATIVE_', '')
      : event.sorts[0].prop.startsWith('IMPORTED') ? event.sorts[0].prop.replace('IMPORTED_', '') : event.sorts[0].prop;

    this.filterParams.pagination.sortBy =
      colName === 'DETAILS' ? 'NAME' : colName === 'AGENT' ? 'CREATOR_LOGIN' : colName;

    this.filterParams.pagination.order = event.sorts[0].dir.toUpperCase();
    this.filterParams.pagination.offset = 0;
    this.contactsService.fetchDataAction(this.filterParams);
  }

  toggleChecking(col) {
    const isChecked = this.isChecked(col);

    if (isChecked) {
      this.columns = this.columns.filter(c => {
        return c.key !== col.key;
      });
    } else {
      this.columns = [...this.columns, col];
    }

    this.store.dispatch(new UpdateColumnsAction(this.columns));
  }

  isChecked(col) {
    return this.columns.find(c => {
      return c.key === col.key;
    });
  }

  setPage(pageInfo) {
    this.filterParams.pagination.offset = pageInfo.offset * this.filterParams.pagination.limit;
    this.contactsService.fetchDataAction(this.filterParams);
  }

  changePagination(event) {
    this.filterParams.pagination.offset = 0;
    this.filterParams.pagination.limit = event.value;
    this.contactsService.fetchDataAction(this.filterParams);
  }

  onUseFilters() {
    this.store.dispatch(new ClearPageOffsetAction(0));
    this.store.dispatch(new SetFilterTagsAction());
    this.contactsService.fetchDataAction(this.tempPageFilters, 0);
  }

  changeDateRange(event) {
    this.contactsService.onChangeRange(event);
    this.contactsService.fetchDataAction(this.tempPageFilters);
  }

  getRangeTagName(rangeFilter: { dateFrom: moment.Moment | string; dateTo: moment.Moment | string }) {
    const dateFrom = `${moment(rangeFilter.dateFrom).format('L')}`;
    const dateTo = `${moment(rangeFilter.dateTo).format('L')}`;

    return dateFrom === dateTo ? dateTo : `${dateFrom} - ${dateTo}`;
  }

  getDateFormated(date: string) {
    return moment(date).format(DATETIME_FORMATS.us);
  }

  getEventTypeLabel(statusName: string) {
    return this.contactsService.getEventTypeLabel(statusName);
  }

  getCallLengthLabel(callLength: number) {
    const minutes = Math.floor(callLength / 60);
    const seconds = callLength - minutes * 60;
    return minutes === 0 && seconds === 0 ? '-' : `${minutes}min ${seconds}sec`;
  }

  buttonClick(event) {
    //future feature event
  }

  openPicker() {
    this.datePickerService.toggleDatePicker({
      open: 'right',
      show: true,
      positionX: this.iconCalendar.nativeElement.offsetLeft,
      positionY: this.iconCalendar.nativeElement.offsetTop
    });
  }

  addFilter() {
    this.contactsService.showFilters();
    this.contactsService.showSearchFilters();
    this.contactsService.snapshotCallFinishType();
  }

  getRelatedContactNames(relatedContacts) {
    return relatedContacts.map(contact => {
      return this.getContactNamePhrase(contact);
    });
  }

  getContactNamePhrase(contact) {
    return `${contact.firstName ? ' ' + contact.firstName : 'UNKNOWN'}${
      contact.lastName ? ' ' + contact.lastName : ''
    }`;
  }
}
